import { useEffect, useRef, useCallback, useState } from "react";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/integrations/supabase/client";
import { useQuery } from "@tanstack/react-query";
import { SECURITY_DEFAULTS } from "@/lib/securityPolicy";


interface UseSessionTimeoutOptions {
  onTimeout: () => void;
  onWarning?: () => void;
  warningTimeBeforeTimeout?: number; // in seconds
}

export function useSessionTimeout({ 
  onTimeout, 
  onWarning,
  warningTimeBeforeTimeout = 60 
}: UseSessionTimeoutOptions) {
  const { user, profile, isAdmin } = useAuth();
  const [showWarning, setShowWarning] = useState(false);
  const [remainingSeconds, setRemainingSeconds] = useState(0);
  const lastActivityRef = useRef<number>(Date.now());
  const timeoutIdRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const warningIdRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const countdownIdRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Fetch centrally-managed session policy for this organization
  const { data: orgSettings } = useQuery({
    queryKey: ["session-policy", profile?.organization_id],
    queryFn: async () => {
      if (!profile?.organization_id) return null;
      const [{ data: sec }, { data: org }] = await Promise.all([
        supabase
          .from("security_settings")
          .select("session_timeout_minutes, privileged_session_timeout_minutes")
          .eq("organization_id", profile.organization_id)
          .maybeSingle(),
        supabase
          .from("organization_settings")
          .select("session_timeout_minutes")
          .eq("organization_id", profile.organization_id)
          .maybeSingle(),
      ]);
      return {
        session_timeout_minutes:
          sec?.session_timeout_minutes ?? org?.session_timeout_minutes ?? null,
        privileged_session_timeout_minutes: sec?.privileged_session_timeout_minutes ?? null,
      };
    },
    enabled: !!profile?.organization_id,
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
  });

  const generalMinutes =
    orgSettings?.session_timeout_minutes ?? SECURITY_DEFAULTS.sessionTimeoutMinutes;
  const privilegedMinutes =
    orgSettings?.privileged_session_timeout_minutes ??
    SECURITY_DEFAULTS.privilegedSessionTimeoutMinutes;

  // Privileged users get the tighter timeout
  const timeoutMinutes = isAdmin ? Math.min(privilegedMinutes, generalMinutes) : generalMinutes;

  const timeoutMs = timeoutMinutes * 60 * 1000;
  const warningMs = warningTimeBeforeTimeout * 1000;

  const resetTimers = useCallback(() => {
    lastActivityRef.current = Date.now();
    setShowWarning(false);

    // Clear existing timers
    if (timeoutIdRef.current) clearTimeout(timeoutIdRef.current);
    if (warningIdRef.current) clearTimeout(warningIdRef.current);
    if (countdownIdRef.current) clearInterval(countdownIdRef.current);

    if (!user) return;

    // Set warning timer
    warningIdRef.current = setTimeout(() => {
      setShowWarning(true);
      setRemainingSeconds(warningTimeBeforeTimeout);
      onWarning?.();
      
      // Start countdown
      countdownIdRef.current = setInterval(() => {
        setRemainingSeconds((prev) => {
          if (prev <= 1) {
            if (countdownIdRef.current) clearInterval(countdownIdRef.current);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }, timeoutMs - warningMs);

    // Set logout timer
    timeoutIdRef.current = setTimeout(() => {
      setShowWarning(false);
      onTimeout();
    }, timeoutMs);
  }, [user, timeoutMs, warningMs, warningTimeBeforeTimeout, onTimeout, onWarning]);

  const handleActivity = useCallback(() => {
    // Throttle activity handling to prevent excessive resets
    const now = Date.now();
    if (now - lastActivityRef.current > 1000) {
      resetTimers();
    }
  }, [resetTimers]);

  const extendSession = useCallback(() => {
    resetTimers();
  }, [resetTimers]);

  useEffect(() => {
    if (!user) return;

    // Set up activity listeners
    const events = ["mousedown", "mousemove", "keydown", "scroll", "touchstart", "click"];
    events.forEach((event) => {
      window.addEventListener(event, handleActivity, { passive: true });
    });

    // Initial timer setup
    resetTimers();

    return () => {
      events.forEach((event) => {
        window.removeEventListener(event, handleActivity);
      });
      if (timeoutIdRef.current) clearTimeout(timeoutIdRef.current);
      if (warningIdRef.current) clearTimeout(warningIdRef.current);
      if (countdownIdRef.current) clearInterval(countdownIdRef.current);
    };
  }, [user, handleActivity, resetTimers]);

  return {
    showWarning,
    remainingSeconds,
    extendSession,
    timeoutMinutes,
  };
}
